export const CALENDLY_URL =
  'https://calendly.com/eulergrowth-systems/llamada-de-demo'

export const controlPanel = [
  { label: 'MER objetivo', value: '3.4x', note: 'blended, no por plataforma' },
  { label: 'LTV / CAC', value: '> 3.0', note: 'medido a 90 días' },
  { label: 'Recompra 60d', value: '31%', note: 'vía email + SMS' },
  { label: 'AOV', value: '+18%', note: 'bundles y suscripción' },
]

export type Pain = {
  number: string
  title: string
  body: string
  symptom: string
}

export const pains: Pain[] = [
  {
    number: '1',
    title: 'Un CPA sangriento que se come tu margen',
    body:
      'Vendes más que nunca y cierras el mes con menos caja. Cada punto que sube el CPA lo pagas del margen bruto, no del presupuesto de medios. Cuando el coste de adquirir se acerca al margen del primer pedido, escalar deja de ser crecimiento: es comprar facturación con tu propio dinero.',
    symptom: 'Facturación arriba, MER abajo.',
  },
  {
    number: '2',
    title: 'Tu negocio depende del humor del algoritmo de Meta',
    body:
      'Una cuenta restringida, una actualización de subasta o tres días de fatiga creativa y tu facturación cae un 40% sin que nada haya cambiado en tu producto. No tienes un problema de puja: tienes un problema de dependencia de un solo canal y de un volumen creativo que no aguanta el ritmo al que Meta quema ángulos.',
    symptom: 'Un solo canal sostiene el 80% de tus ventas.',
  },
  {
    number: '3',
    title: 'Nadie vuelve a comprar: tu LTV está plano',
    body:
      'En suplementos el negocio real está en el pedido 2, 3 y 4. Si el 70% de tus clientes compra una vez, estás financiando el crecimiento con adquisición pura y compitiendo por CPA con marcas que sí monetizan su base. Sin recompra, no hay techo de escala: hay una cinta de correr.',
    symptom: 'LTV de un solo pedido y cero previsibilidad.',
  },
]

export type SystemStep = {
  phase: string
  title: string
  body: string
  levers: string[]
}

export const systemSteps: SystemStep[] = [
  {
    phase: 'Fase 01',
    title: 'Paid Media con criterio de P&L',
    body:
      'Reestructuramos la cuenta para optimizar MER blended y contribución, no ROAS de plataforma. Presupuesto asignado por margen real de cada SKU y ángulo.',
    levers: ['Estructura por margen', 'Incrementalidad', 'Escala por MER'],
  },
  {
    phase: 'Fase 02',
    title: 'Creativos UGC en volumen industrial',
    body:
      'El creativo es la nueva segmentación. Producimos y testamos ángulos nuevos cada semana —problema, ingrediente, prueba, autoridad— para que el CPA no dependa de un solo anuncio ganador.',
    levers: ['12–20 conceptos/mes', 'Testing por ángulo', 'Hooks validados'],
  },
  {
    phase: 'Fase 03',
    title: 'Email, SMS y suscripción que recuperan el margen',
    body:
      'Flows de bienvenida, reposición y winback calibrados al ciclo de consumo del producto. Aquí es donde el LTV sube y el CPA que pagaste arriba deja de doler.',
    levers: ['Flows por ciclo', 'Bundles y AOV', 'Suscripción'],
  },
]

export type ComparisonRow = {
  dimension: string
  agency: string
  partner: string
}

export const comparison: ComparisonRow[] = [
  {
    dimension: 'Métrica que reporta',
    agency: 'ROAS de plataforma en un PDF mensual',
    partner: 'MER blended, contribución por SKU y LTV a 90 días',
  },
  {
    dimension: 'Alcance del trabajo',
    agency: 'Solo la cuenta publicitaria',
    partner: 'Adquisición, oferta, creativo y retención en un mismo P&L',
  },
  {
    dimension: 'Modelo económico',
    agency: 'Fee fijo por gestionar gasto',
    partner: 'Fee base + variable ligado al crecimiento real',
  },
  {
    dimension: 'Quién ejecuta',
    agency: 'Un junior con 9 cuentas a la vez',
    partner: 'Un operador senior con 4 marcas máximo',
  },
  {
    dimension: 'Compromiso',
    agency: 'Contrato de 12 meses cerrado',
    partner: '90 días con objetivos definidos por escrito',
  },
]

export const callSteps = [
  {
    number: '1',
    title: 'Diagnóstico de números',
    body:
      'Revisamos juntos MER, CPA, AOV, tasa de recompra y márgenes reales. En 10 minutos sabemos dónde se está fugando el dinero.',
  },
  {
    number: '2',
    title: 'Cuantificamos el gap',
    body:
      'Te muestro qué facturación deja tu estructura actual sobre la mesa y qué palancas la desbloquean en 90 días.',
  },
  {
    number: '3',
    title: 'Plan o salida limpia',
    body:
      'Si encaja, te presento el plan de escala. Si no, te llevas el diagnóstico y ejecútalo con tu equipo. Sin seguimiento comercial.',
  },
]

export type Reason = {
  title: string
  body: string
  proof: string
}

export const leadReason: Reason = {
  title:
    'Solo trabajamos suplementos y salud, y eso cambia todo el punto de partida',
  body:
    'No empezamos aprendiendo tu categoría. Ya sabemos cuál es el ciclo de consumo de un bote de 30 o 60 días, en qué semana hay que disparar el flow de reposición, qué ángulos creativos pasan revisión y cuáles queman la cuenta, y en qué margen bruto deja de tener sentido subir la puja. Lo que en una agencia generalista son tres meses de curva de aprendizaje pagados con tu presupuesto, aquí son decisiones que se toman en la primera semana.',
  proof:
    'Máximo 4 marcas en cartera al mismo tiempo. Cero clientes fuera del vertical.',
}

export const supportingReasons: Reason[] = [
  {
    title: 'Operamos sobre tu P&L, no sobre el panel de Meta',
    body:
      'Antes de tocar una campaña reconstruimos el modelo: margen por SKU, coste de envío, tasa de devolución y umbral real de CPA. A partir de ahí se decide dónde va cada euro. Sin ese modelo, escalar es apostar.',
    proof: 'Decisiones sobre MER blended y contribución.',
  },
  {
    title: 'El crecimiento sale de dos palancas, no de una',
    body:
      'Bajar el CPA tiene un suelo; subir el LTV no. Por eso adquisición y retención se operan juntas: el margen que gana el email es el que financia volver a subir la inversión en medios sin romper la rentabilidad.',
    proof: 'Paid Media + UGC + Email bajo un mismo responsable.',
  },
  {
    title: 'Nuestro upside depende del tuyo',
    body:
      'Fee base ajustado más variable ligado al crecimiento real, con objetivos por escrito a 90 días. Si tu contribución no sube, nosotros tampoco cobramos la parte que importa. Ese es el filtro con el que aceptamos marcas.',
    proof: 'Objetivos definidos y revisables a 90 días.',
  },
]

export const qualifiers = [
  'Marcas de suplementos y salud',
  'Facturación desde 40k€/mes',
  'Inversión mínima de 15k€/mes en medios',
]
